/**
 * pages/SharePage.tsx
 * Public read-only view of a shared note.
 * Route: /note/:noteId/share?token=xxx
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Lock, Calendar, Hash, ExternalLink, Loader } from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', red:'#DC2626',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

interface SharedNote {
  id: string; title: string; body: string; tags: string[];
  folderName: string | null; folderColor: string | null;
  updatedAt: string; ownerName: string;
}

const SharePage: React.FC = () => {
  useParams<{ noteId: string }>();
  const token      = new URLSearchParams(window.location.search).get('token');
  const apiUrl     = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const [note,    setNote]    = useState<SharedNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid share link — token missing.'); setLoading(false); return; }
    fetch(`${apiUrl}/notes/public/${token}`)
      .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.message || 'Not found'); }))
      .then(data => { setNote(data); setLoading(false); })
      .catch(e => { setError(e.message || 'This note is no longer available.'); setLoading(false); });
  }, [token, apiUrl]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.surface }}>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
        <Loader size={28} color={C.amber300} style={{ animation:'spin .8s linear infinite' }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>Loading note…</p>
      </div>
    </div>
  );

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error || !note) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:C.surface, padding:'24px' }}>
      <div style={{ maxWidth:400, textAlign:'center' }}>
        <div style={{ width:60, height:60, borderRadius:18, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
          <Lock size={26} color={C.red}/>
        </div>
        <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>
          Note not available
        </h1>
        <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.7, marginBottom:24 }}>
          {error || 'This note has been unshared or the link is invalid.'}
        </p>
        <a href="/" style={{ display:'inline-flex', padding:'11px 24px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
          Open Jötr
        </a>
      </div>
    </div>
  );

  // ── Note ──────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; color: #1A1814; }
        .note-body h1,.note-body h2,.note-body h3 { font-family: 'Sora',sans-serif; font-weight: 700; margin: 24px 0 10px; color: #1A1814; }
        .note-body h1 { font-size: clamp(20px,3vw,26px); }
        .note-body h2 { font-size: clamp(17px,2.5vw,22px); }
        .note-body h3 { font-size: clamp(15px,2vw,18px); }
        .note-body p  { font-size: 16px; line-height: 1.85; color: #3D3A34; margin-bottom: 14px; }
        .note-body ul,.note-body ol { padding-left: 24px; margin-bottom: 14px; }
        .note-body li { font-size: 16px; line-height: 1.8; color: #3D3A34; margin-bottom: 6px; }
        .note-body strong { font-weight: 700; color: #1A1814; }
        .note-body em { font-style: italic; }
        .note-body code { background: #FFF4E0; color: #C4811A; padding: 2px 6px; border-radius: 4px; font-size: 14px; }
        .note-body blockquote { border-left: 3px solid #F5A623; padding-left: 16px; margin: 16px 0; color: #7A7570; }
        @keyframes spin { to { transform:rotate(360deg) } }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom:`1px solid ${C.border}`, padding:'16px clamp(20px,5vw,60px)', display:'flex', alignItems:'center', justifyContent:'space-between', background:C.white, position:'sticky', top:0, zIndex:10 }}>
        <a href="/" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none' }}>
          <div style={{ width:30, height:30, borderRadius:9, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="14" height="14" viewBox="0 0 180 180" fill="none">
              <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
              <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
              <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
            </svg>
          </div>
          <span style={{ fontFamily:font.head, fontWeight:800, fontSize:18, letterSpacing:'-0.05em', color:C.ink }}>Jötr</span>
        </a>
        <a href="/signup" style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:9, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:13, textDecoration:'none', fontFamily:font.body }}>
          <ExternalLink size={13}/> Try Jötr free
        </a>
      </div>

      {/* Note content */}
      <div style={{ maxWidth:760, margin:'0 auto', padding:'40px clamp(20px,5vw,60px) 80px' }}>
        {/* Meta */}
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, color:C.muted, fontFamily:font.body }}>
            Shared by <strong style={{ color:C.inkLight }}>{note.ownerName}</strong>
          </span>
          <span style={{ width:3, height:3, borderRadius:'50%', background:C.border }}/>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:C.muted, fontFamily:font.body }}>
            <Calendar size={12}/>
            {new Date(note.updatedAt).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}
          </span>
          {note.folderName && (
            <>
              <span style={{ width:3, height:3, borderRadius:'50%', background:C.border }}/>
              <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, fontFamily:font.body }}>
                <div style={{ width:8, height:8, borderRadius:2, background:note.folderColor || C.amber300 }}/>
                <span style={{ color:C.muted }}>{note.folderName}</span>
              </span>
            </>
          )}
        </div>

        {/* Title */}
        <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(24px,5vw,40px)', letterSpacing:'-0.04em', color:C.ink, lineHeight:1.15, marginBottom:16 }}>
          {note.title || 'Untitled note'}
        </h1>

        {/* Tags */}
        {note.tags.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:24 }}>
            {note.tags.map(t => (
              <span key={t} style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, background:C.amber100, border:`1px solid ${C.amber200}` }}>
                <Hash size={10} color={C.amber400}/>
                <span style={{ fontSize:11, fontWeight:700, color:C.amber500, fontFamily:font.body }}>{t}</span>
              </span>
            ))}
          </div>
        )}

        <div style={{ height:1, background:C.border, marginBottom:32 }}/>

        {/* Body */}
        <div className="note-body" dangerouslySetInnerHTML={{ __html: note.body || '<p style="color:#7A7570">This note has no content.</p>' }}/>

        {/* CTA */}
        <div style={{ marginTop:56, padding:'24px 28px', borderRadius:16, background:C.amber100, border:`1px solid ${C.amber200}`, textAlign:'center' }}>
          <p style={{ fontFamily:font.head, fontWeight:800, fontSize:18, color:C.ink, marginBottom:8 }}>Keep your own notes in Jötr</p>
          <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, marginBottom:16 }}>Free forever. Full-text search. Works offline.</p>
          <a href="/signup" style={{ display:'inline-flex', padding:'11px 24px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
            Get started free →
          </a>
        </div>
      </div>
    </>
  );
};

export default SharePage;